import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tabs,
  Tab,
  ButtonGroup,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  School as SchoolIcon,
  CloudUpload as CloudIcon,
  Settings as SettingsIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { moodleApi, MoodleSource } from '../services/moodle-api';
import { s3BucketsApi, S3BucketConfig, S3BucketEnvironment } from '../services/s3-buckets-api';
import { CMI5ActivityBrowserDialog } from './CMI5ActivityBrowserDialog';
import { S3ImportDialog } from './S3ImportDialog';
import { cmi5ConfigService } from '../services/cmi5-config';

interface CMI5ActivityBrowserProps {
  onBack: () => void;
}

// Unified source interface
type UnifiedSource =
  | { type: 'moodle'; source: MoodleSource }
  | { type: 's3'; bucket: S3BucketConfig; environment: S3BucketEnvironment };

export const CMI5ActivityBrowser: React.FC<CMI5ActivityBrowserProps> = ({ onBack }) => {
  const [tabValue, setTabValue] = useState<'moodle' | 's3'>('moodle');
  const [moodleSources, setMoodleSources] = useState<MoodleSource[]>([]);
  const [s3Buckets, setS3Buckets] = useState<S3BucketConfig[]>([]);
  const [s3Environments, setS3Environments] = useState<S3BucketEnvironment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // CMI5 Activity Browser Dialog state
  const [cmi5BrowserOpen, setCmi5BrowserOpen] = useState(false);
  const [cmi5BrowsingSource, setCmi5BrowsingSource] = useState<UnifiedSource | null>(null);
  const [cmi5Activities, setCmi5Activities] = useState<Array<{
    tenantId: string;
    courseId: string;
    activityId: number;
    activityName: string;
    cmi5activityid?: string;
    moodleCourseId?: number;
  }>>([]);
  const [loadingCmi5Activities, setLoadingCmi5Activities] = useState(false);

  // S3 Import Dialog state
  const [s3ImportDialogOpen, setS3ImportDialogOpen] = useState(false);
  const [s3ImportBucket, setS3ImportBucket] = useState<{ bucket: S3BucketConfig; environment: S3BucketEnvironment } | null>(null);

  useEffect(() => {
    loadSources();
  }, [tabValue]);

  const loadSources = async () => {
    setLoading(true);
    setError(null);
    try {
      if (tabValue === 'moodle') {
        const data = await moodleApi.listSources();
        setMoodleSources(data);
      } else {
        const [buckets, environments] = await Promise.all([
          s3BucketsApi.listBuckets(),
          s3BucketsApi.listEnvironments(),
        ]);
        setS3Buckets(buckets);
        setS3Environments(environments);
      }
    } catch (err) {
      console.error(`Failed to load ${tabValue} sources:`, err);
      setError(`Failed to load ${tabValue} sources`);
    } finally {
      setLoading(false);
    }
  };

  const handleBrowseCMI5Activities = async (unifiedSource: UnifiedSource) => {
    setCmi5BrowsingSource(unifiedSource);
    setLoadingCmi5Activities(true);
    setError(null);

    try {
      if (unifiedSource.type === 'moodle') {
        const activities = await cmi5ConfigService.listCMI5Activities(unifiedSource.source.id);
        setCmi5Activities(activities);
        setCmi5BrowserOpen(true);
      } else {
        // For S3 sources, list packages from the bucket/environment
        const packages = await s3BucketsApi.listCMI5Packages(
          unifiedSource.bucket.id,
          unifiedSource.environment.id
        );

        // Convert packages to activity format
        const activities = packages.map((pkg, index) => ({
          tenantId: 's3', // Special marker for S3 sources
          courseId: pkg.key, // Use S3 key as courseId
          activityId: index,
          activityName: pkg.name,
          size: pkg.size,
          lastModified: pkg.lastModified,
          versionInfo: pkg.versionInfo, // Include version information
        }));

        setCmi5Activities(activities);
        setCmi5BrowserOpen(true);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load CMI5 activities');
    } finally {
      setLoadingCmi5Activities(false);
    }
  };

  const handleOpenImportDialog = (bucket: S3BucketConfig, environment: S3BucketEnvironment) => {
    setS3ImportBucket({ bucket, environment });
    setS3ImportDialogOpen(true);
  };

  // For S3, create bucket/environment combinations
  const s3BucketEnvironments = s3Environments.map(env => {
    const bucket = s3Buckets.find(b => b.id === env.bucketId);
    return bucket ? { bucket, environment: env } : null;
  }).filter((item): item is { bucket: S3BucketConfig; environment: S3BucketEnvironment } => item !== null);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ mb: 2 }}
        >
          Back
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SchoolIcon sx={{ fontSize: 40 }} color="primary" />
            <Box>
              <Typography variant="h4" component="h1">
                CMI5 Activity Browser
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Browse and configure CMI5 activities from Moodle or S3 sources
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={loadSources} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Tab selector */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, value) => setTabValue(value)}>
          <Tab
            icon={<SchoolIcon />}
            label="Moodle Sources"
            value="moodle"
            iconPosition="start"
          />
          <Tab
            icon={<CloudIcon />}
            label="S3 Sources"
            value="s3"
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress size={60} />
        </Box>
      ) : (tabValue === 'moodle' ? moodleSources.length === 0 : s3BucketEnvironments.length === 0) ? (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              {tabValue === 'moodle' ? (
                <SchoolIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              ) : (
                <CloudIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              )}
              <Typography variant="h6" gutterBottom>
                No {tabValue === 'moodle' ? 'Moodle Sources' : 'S3 Environments'} Configured
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {tabValue === 'moodle'
                  ? 'Configure a Moodle source in Settings to browse CMI5 activities.'
                  : 'Configure S3 buckets and environments in the S3 Manager to browse CMI5 packages.'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Select a {tabValue === 'moodle' ? 'Moodle Source' : 'S3 Environment'}
            </Typography>
          </Box>
          <List>
            {tabValue === 'moodle' ? (
              moodleSources.map((source) => {
                const unifiedSource: UnifiedSource = { type: 'moodle', source };
                const isCurrentlyBrowsing =
                  loadingCmi5Activities &&
                  cmi5BrowsingSource?.type === 'moodle' &&
                  cmi5BrowsingSource.source.id === source.id;

                return (
                  <Card key={source.id} sx={{ mb: 2 }}>
                    <ListItem
                      secondaryAction={
                        <Button
                          variant="contained"
                          onClick={() => handleBrowseCMI5Activities(unifiedSource)}
                          disabled={isCurrentlyBrowsing}
                        >
                          {isCurrentlyBrowsing ? 'Loading...' : 'Browse Packages'}
                        </Button>
                      }
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6">{source.name}</Typography>
                            <Chip
                              label={source.status}
                              size="small"
                              color={source.status === 'active' ? 'success' : 'default'}
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Namespace:</strong> {source.namespace}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Pod:</strong> {source.podName}
                            </Typography>
                            {source.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {source.description}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  </Card>
                );
              })
            ) : (
              s3BucketEnvironments.map(({ bucket, environment }) => {
                const unifiedSource: UnifiedSource = { type: 's3', bucket, environment };
                const isCurrentlyBrowsing =
                  loadingCmi5Activities &&
                  cmi5BrowsingSource?.type === 's3' &&
                  cmi5BrowsingSource.bucket.id === bucket.id &&
                  cmi5BrowsingSource.environment.id === environment.id;

                return (
                  <Card key={`${bucket.id}-${environment.id}`} sx={{ mb: 2 }}>
                    <ListItem
                      secondaryAction={
                        <ButtonGroup variant="contained">
                          <Button
                            onClick={() => handleBrowseCMI5Activities(unifiedSource)}
                            disabled={isCurrentlyBrowsing}
                          >
                            {isCurrentlyBrowsing ? 'Loading...' : 'Browse Packages'}
                          </Button>
                          <Button
                            onClick={() => handleOpenImportDialog(bucket, environment)}
                            startIcon={<UploadIcon />}
                          >
                            Upload
                          </Button>
                        </ButtonGroup>
                      }
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6">{bucket.name} / {environment.name}</Typography>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Bucket:</strong> {bucket.bucket} ({bucket.region})
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Environment Prefix:</strong> {environment.prefix}
                            </Typography>
                            {environment.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {environment.description}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  </Card>
                );
              })
            )}
          </List>
        </Box>
      )}

      {/* CMI5 Activity Browser Dialog */}
      {cmi5BrowsingSource && (
        <CMI5ActivityBrowserDialog
          open={cmi5BrowserOpen}
          onClose={() => {
            setCmi5BrowserOpen(false);
            setCmi5BrowsingSource(null);
            setCmi5Activities([]);
          }}
          sourceId={
            cmi5BrowsingSource.type === 'moodle'
              ? cmi5BrowsingSource.source.id
              : `${cmi5BrowsingSource.bucket.id}:${cmi5BrowsingSource.environment.id}`
          }
          sourceName={
            cmi5BrowsingSource.type === 'moodle'
              ? cmi5BrowsingSource.source.name
              : `${cmi5BrowsingSource.bucket.name} / ${cmi5BrowsingSource.environment.name}`
          }
          activities={cmi5Activities}
          sourceEnvironmentId={
            cmi5BrowsingSource.type === 'moodle'
              ? cmi5BrowsingSource.source.rangeosEnvironmentId
              : undefined
          }
          sourceType={cmi5BrowsingSource.type}
          s3BucketId={
            cmi5BrowsingSource.type === 's3'
              ? cmi5BrowsingSource.bucket.id
              : undefined
          }
          s3EnvironmentId={
            cmi5BrowsingSource.type === 's3'
              ? cmi5BrowsingSource.environment.id
              : undefined
          }
        />
      )}

      {/* S3 Import Dialog */}
      {s3ImportBucket && (
        <S3ImportDialog
          open={s3ImportDialogOpen}
          onClose={() => {
            setS3ImportDialogOpen(false);
            setS3ImportBucket(null);
          }}
          bucketId={s3ImportBucket.bucket.id}
          environmentId={s3ImportBucket.environment.id}
          bucketName={s3ImportBucket.bucket.name}
          environmentName={s3ImportBucket.environment.name}
          onSuccess={() => {
            // Optionally refresh the activities list if we're currently browsing this bucket
            if (
              cmi5BrowsingSource?.type === 's3' &&
              cmi5BrowsingSource.bucket.id === s3ImportBucket.bucket.id &&
              cmi5BrowsingSource.environment.id === s3ImportBucket.environment.id
            ) {
              handleBrowseCMI5Activities(cmi5BrowsingSource);
            }
          }}
        />
      )}
    </Container>
  );
};
